import { stringify } from 'query-string';

/**
 * Transforms the given resource and query into a formatted API URL.
 *
 * Handles special cases for certain resources:
 * - 'users-answers': Maps to `/admin/interactive-articles/users-answers`.
 * - Resources starting with 'crosswords/found-items': Maps to `/admin/crosswords/reports/found-items`.
 * - Resources starting with 'crosswords/rewards': Maps to `/admin/crosswords/reports/rewards`.
 * - All other resources: Uses the default pattern.
 *
 * @param {string} apiUrl - The base API URL.
 * @param {string} resource - The resource path or identifier.
 * @param {Object} query - The query parameters to be stringified and appended.
 * @param {boolean} [transformQuery=true] - If true (default), the query object will be converted to a query string using stringify; if false, the query value will be used as provided (useful when already stringified).
 * @returns {string} The transformed URL.

 * @example
 * // Object query will be stringified
 * transformURL('https://api', 'users-answers', { page: 1, limit: 10 });
 * // -> "https://api/admin/interactive-articles/users-answers?page=1&limit=10"
 *
 * @example
 * // Resource with suffix and pre-stringified query (no transformation)
 * transformURL('https://api', 'crosswords/found-items/xlsx', 'format=xlsx', false);
 * // -> "https://api/admin/crosswords/reports/found-items/xlsx?format=xlsx"
 */
const transformURL = (apiUrl, resource, query, transformQuery = true) => {
  if (resource === 'users-answers') {
    return `${apiUrl}/admin/interactive-articles/${resource}?${transformQuery ? stringify(query) : query}`;
  }
  if (resource.startsWith('crosswords/found-items')) {
    const suffix = resource.slice('crosswords/found-items'.length); // e.g. '/xlsx'
    return `${apiUrl}/admin/crosswords/reports/found-items${suffix}?${transformQuery ? stringify(query) : query}`;
  }
  if (resource.startsWith('crosswords/rewards')) {
    const suffix = resource.slice('crosswords/rewards'.length); // e.g. '/xlsx'
    return `${apiUrl}/admin/crosswords/reports/rewards${suffix}?${transformQuery ? stringify(query) : query}`;
  }

  return `${apiUrl}/${resource}?${transformQuery ? stringify(query) : query}`;
};

export default transformURL;
